import type { FareCode } from "@silversea-ssc/web-fares";
import { farePriority } from "@silversea-ssc/web-fares";
import { EnhancedAvailablePriceWithFareCode } from "src/ssc-api/prices/types";
import { match, P } from "ts-pattern";
import { isPresent } from "./checks";
import { msg } from "@lingui/core/macro";
import { MessageDescriptor } from "@lingui/core";

export const fareCodeLabelForPrice = (
  price: EnhancedAvailablePriceWithFareCode,
  labelFormat: "with-from" | "without-from"
): MessageDescriptor =>
  match(price)
    .with({ type: "waitlist" }, () => fareCodeLabel(price.webFareCode))
    .with(
      { type: P.union("price", "discounted") },
      (price) =>
        labelFormat == "with-from"
          ? fareCodeFromLabel(price.webFareCode)
          : fareCodeLabel(price.webFareCode) //TODO review the label for discounted amount
    )
    .exhaustive();

export const fareCodeLabel = (fareCode: FareCode) => {
  const fareCodeLabelByFareCode: Record<FareCode, MessageDescriptor> = {
    DoorToDoor: msg({ id: "fare-door-to-door" }),
    PortToPort: msg({ id: "fare-port-to-port" }),
    DoorToDoorV2: msg({ id: "fare-door-to-door" }),
    PortToPortV2: msg({ id: "fare-port-to-port" }),
    Essential: msg({ id: "fare-essential" })
  };
  return fareCodeLabelByFareCode[fareCode];
};

const fareCodeFromLabel = (fareCode: FareCode) => {
  const fareCodeFromLabelByFareCode: Record<FareCode, MessageDescriptor> = {
    DoorToDoor: msg`DoorToDoor From`,
    PortToPort: msg`PortToPort From`,
    DoorToDoorV2: msg`DoorToDoor From`,
    PortToPortV2: msg`PortToPort From`,
    Essential: msg`Essential From`
  };
  return fareCodeFromLabelByFareCode[fareCode];
};

export const fareByPriority = (a: FareCode, b: FareCode) =>
  farePriority[a] - farePriority[b];

export const fareContentByFareCodes = <
  T extends { fareCodes?: (string | undefined)[] }
>(
  fareContents: (T | undefined)[] | undefined,
  fareCode: FareCode
): T | undefined =>
  (fareContents || []).filter(isPresent).find(withFareCode(fareCode));

export const isD2DFareCode = (fareCode: FareCode) =>
  fareCode == "DoorToDoor" || fareCode == "DoorToDoorV2";

const withFareCode =
  <T extends { fareCodes?: (string | undefined)[] | (FareCode | undefined)[] }>(
    fareCode: FareCode
  ) =>
  (object: T): boolean => {
    return (object.fareCodes || []).includes(fareCode);
  };
